import React, { useState } from "react";
import { Link } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SearchIcon from "@mui/icons-material/Search";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import HealthAndSafetyOutlinedIcon from "@mui/icons-material/HealthAndSafetyOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DataObjectIcon from "@mui/icons-material/DataObject";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import FamilyRestroomOutlinedIcon from "@mui/icons-material/FamilyRestroomOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";

import {
  accentText,
  mutedText,
  filledButton,
  outlineButton,
  Browser,
  Phone,
  TourBar,
  FeatureTiles,
  TourStep,
  InternalsGrid,
  useActiveStep,
} from "../components/Walkthrough";

import cardArt from "../assets/graphics/HealthSearch_Card.svg";
import resultsLight from "../assets/demo_screenshots/healthsearch/desktop-results-light.webp";
import resultsDark from "../assets/demo_screenshots/healthsearch/desktop-results-dark.webp";
import insurerLight from "../assets/demo_screenshots/healthsearch/desktop-insurer-light.webp";
import insurerDark from "../assets/demo_screenshots/healthsearch/desktop-insurer-dark.webp";
import methodLight from "../assets/demo_screenshots/healthsearch/desktop-method-light.webp";
import methodDark from "../assets/demo_screenshots/healthsearch/desktop-method-dark.webp";
import searchLight from "../assets/demo_screenshots/healthsearch/phone-search-light.webp";
import searchDark from "../assets/demo_screenshots/healthsearch/phone-search-dark.webp";
import phoneResultsLight from "../assets/demo_screenshots/healthsearch/phone-results-light.webp";
import phoneResultsDark from "../assets/demo_screenshots/healthsearch/phone-results-dark.webp";
import medicinesLight from "../assets/demo_screenshots/healthsearch/phone-medicines-light.webp";
import medicinesDark from "../assets/demo_screenshots/healthsearch/phone-medicines-dark.webp";
import suggestLight from "../assets/demo_screenshots/healthsearch/phone-suggest-light.webp";
import suggestDark from "../assets/demo_screenshots/healthsearch/phone-suggest-dark.webp";
import ideasLight from "../assets/demo_screenshots/healthsearch/phone-ideas-light.webp";
import ideasDark from "../assets/demo_screenshots/healthsearch/phone-ideas-dark.webp";
import emergencyLight from "../assets/demo_screenshots/healthsearch/phone-emergency-light.webp";
import emergencyDark from "../assets/demo_screenshots/healthsearch/phone-emergency-dark.webp";

// Desktop screenshots are 1600x1000 (the methodology one 1200x912); phone ones use the phone frame's size.
const WIDE = { width: 1600, height: 1000, browser: true };

export const shots = {
  results: { light: resultsLight, dark: resultsDark },
  insurer: { light: insurerLight, dark: insurerDark },
  method: { light: methodLight, dark: methodDark },
  search: { light: searchLight, dark: searchDark },
  phoneResults: { light: phoneResultsLight, dark: phoneResultsDark },
  medicines: { light: medicinesLight, dark: medicinesDark },
  suggest: { light: suggestLight, dark: suggestDark },
  ideas: { light: ideasLight, dark: ideasDark },
  emergency: { light: emergencyLight, dark: emergencyDark },
};

// The tour follows one search, "knee MRI near Boston", from the question to the price and the
// medicines, then the guardrails and where every number comes from.
const steps = [
  {
    id: "search",
    label: "Search",
    title: "Ask in plain words",
    body: "Describe a health concern the way you'd say it, typos and all. Add a Massachusetts town or ZIP code to bring in nearby hospitals, or name a test or procedure to compare what they charge.",
    detail: "Each search is matched against about 1,000 MedlinePlus health topics by its words and by its meaning.",
    screens: [{ shot: shots.search, caption: "The search page", alt: "HealthSearch's search box with example searches such as knee MRI near Boston" }],
  },
  {
    id: "prices",
    label: "Prices",
    title: "See what every hospital charges",
    body: "\"knee MRI near Boston\" lists 31 hospitals that publish a price, with cash prices from $403 to $4,222, more than 10x apart for the same scan. Each card shows the price, how it compares with the others, CMS star ratings, a map, and a link to the hospital's own price file.",
    detail: "The overview on the left is quoted word for word from MedlinePlus, never written by a model.",
    screens: [{ ...WIDE, shot: shots.results, caption: "Knee MRI near Boston", alt: "Results for knee MRI near Boston: an overview quoted from MedlinePlus, the AI suggestions card, and hospital cards starting at $449" }],
  },
  {
    id: "insurer",
    label: "Insurer",
    title: "Your insurer's rate, not just the list price",
    body: "Pick one of 16 insurers and every card switches to the rate that hospital has negotiated with that plan, along with the range across the hospitals listed. Sort by Recommended, which weighs price 45%, stars 35% and distance 20%, or by closest or highest rated.",
    screens: [{ ...WIDE, shot: shots.insurer, caption: "Blue Cross Blue Shield rates", alt: "The same search with Blue Cross Blue Shield of Massachusetts picked, showing negotiated rates from $210 to $1,205" }],
  },
  {
    id: "medicines",
    label: "Medicines",
    title: "Medicines, with ballpark prices",
    body: "Prescription medicines linked to the search come with a price: pharmacy drugs from Mark Cuban Cost Plus Drugs' published cash prices, and drugs given at a hospital from the hospitals' own files, next to what Medicare pays.",
    screens: [
      { shot: shots.phoneResults, caption: "On a phone", alt: "A hospital card on a phone: Boston Medical Center-Brighton, $449 cash, with ratings and a map" },
      { shot: shots.medicines, caption: "Medicines", alt: "Prescription medicines for a knee MRI search, one line each: meloxicam opened to show $10.63 from Cost Plus Drugs, and injections priced at nearby hospitals" },
    ],
  },
  {
    id: "suggestions",
    label: "Suggestions",
    title: "Self-care ideas, only when you ask",
    body: "A click asks an LLM for over-the-counter options, exercises and diet changes, each with a caution and when to see a doctor. For a procedure it explains what to expect. It's clearly labeled as AI-written, and the search is sent without its town or ZIP code.",
    detail: "How it's kept accurate, private and cheap is in the AI spotlight above.",
    screens: [{ shot: shots.suggest, caption: "Things that might help", alt: "AI-written suggestions: when to see a doctor, and what to expect from a knee MRI" }],
  },
  {
    id: "safety",
    label: "Safety",
    title: "Knows when to stop",
    body: "Searches that sound like an emergency, like chest pain, stroke signs or thoughts of suicide, open with a banner pointing to 911 or 988, and get no prices or suggestions. The phrases come from a hand-written list, not a model, so the banner even works while the server is offline.",
    screens: [{ shot: shots.emergency, caption: "An emergency search", alt: "A search for crushing chest pain showing a banner: This could be a medical emergency. Call 911." }],
  },
  {
    id: "method",
    label: "Method",
    title: "Every number explained",
    body: "A methodology page explains where each number comes from and what it doesn't tell you, and grades every hospital's price file out of 100: is it readable, how many services it covers, are rates in dollars, and is it up to date.",
    screens: [{ shot: shots.method, browser: true, width: 1200, height: 912, caption: "The price file report card", alt: "The price file report card, scoring each Massachusetts hospital's price file out of 100" }],
  },
];

const features = [
  { icon: AutoAwesomeIcon, title: "AI suggestions", text: "An LLM, with guardrails written in code.", href: "#ai" },
  { icon: LocalHospitalOutlinedIcon, title: "Real hospital prices", text: "400k+ prices from 49 Massachusetts hospitals.", href: "#prices" },
  { icon: SearchIcon, title: "Hybrid search", text: "About 1,000 health topics, matched by meaning.", href: "#search" },
  { icon: HealthAndSafetyOutlinedIcon, title: "Careful by design", text: "911 and 988 banners, sources on everything.", href: "#safety" },
];

// The LLM feature, spotlighted: what the model does, and what the code around it guarantees.
const aiPoints = [
  {
    icon: DataObjectIcon,
    title: "Structured output",
    text: "Answers come back as JSON matching a schema: every idea has a kind, a reason, a caution and the MedlinePlus topic it relates to. Picking a procedure switches to a second schema that adds what to expect.",
  },
  {
    icon: FactCheckOutlinedIcon,
    title: "Checked by code",
    text: "The API drops any idea with a dose, a dollar figure in its text, diagnosis-like wording, advice to wait before getting care, or a medicine without a caution. Store prices are separate fields, kept only if plausible and labeled as AI estimates.",
  },
  {
    icon: LockOutlinedIcon,
    title: "Private by design",
    text: "It runs only on a click, and the card says what's sent first. The town or ZIP code is cut out before the call, the search travels in a POST body, and it's never logged.",
  },
  {
    icon: FamilyRestroomOutlinedIcon,
    title: "Fitted to who it's for",
    text: "Say who it's for and an age range, and the ideas and cautions change with them: children's doses checked with a pharmacist, other medicines and falls for 65 and over. Both are optional, one tap, and never saved with the answer.",
  },
  {
    icon: ReportProblemOutlinedIcon,
    title: "Never on emergencies",
    text: "No suggestions next to a 911 or 988 banner: the page doesn't offer them, and the API checks again before calling the model.",
  },
  {
    icon: ErrorOutlineOutlinedIcon,
    title: "Fails in the open",
    text: "If the model is unavailable, the card says so and why. Nothing else on the page depends on the model, and the overview stays quoted from MedlinePlus.",
  },
];

export const tech = ["React", "LLM integration", "FastAPI", "OpenSearch", "Raspberry Pi", "Python & DuckDB"];

const numbers = [
  { value: "49", label: "hospital price files parsed" },
  { value: "400k+", label: "published prices indexed" },
  { value: "0.809", label: "nDCG@10 with hybrid search, up from 0.621" },
  { value: "~50 ms", label: "for a full search, on a Raspberry Pi" },
];

const internals = [
  {
    title: "Self-hosted on a Raspberry Pi",
    text: "The API is FastAPI and the search engine is OpenSearch, both in Docker on a Raspberry Pi 4 at home. Tailscale Funnel publishes the API over HTTPS; OpenSearch is only reachable from the Pi itself. The page runs on my portfolio site on Vercel, and UptimeRobot watches the API.",
  },
  {
    title: "Hybrid search",
    text: "Each search is split into its health part, a Massachusetts place and price words. The health part is matched by keyword, with typo tolerance, and by meaning, with a small open-source embedding model (bge-small-en-v1.5) running on the Pi. Blending the two lifted nDCG@10 from 0.621 to 0.809 on 25 graded symptom searches.",
  },
  {
    title: "Every hospital's price file",
    text: "Hospitals must publish their prices, but every file is different: CSV or JSON, wide or tall, some of them hundreds of megabytes. A Python and DuckDB pipeline on my PC downloads, parses and normalizes them into 49 shoppable services, and scores each file's quality.",
  },
  {
    title: "Data refreshes that can't break search",
    text: "Each data load builds a new, versioned index behind an alias. The new index has to answer real searches correctly before the alias moves to it, so a bad load never reaches visitors.",
  },
  {
    title: "Private by default",
    text: "Health searches are sensitive, so none is kept. Searches and clicks are counted by category only, never with an IP address or the words typed; the rate limiter keeps IPs only in memory, and the search stays out of the page URL and browser history.",
  },
  {
    title: "Measured before optimized",
    text: "A full search answers in about 50 ms on the Pi. Early on, timing each part showed highlighting was most of the cost; dropping it in favor of MedlinePlus's own summaries took OpenSearch from about 94 ms to 26 ms median.",
  },
];

const stepIds = steps.map((s) => s.id);

function HealthSearchProject() {
  const [appTheme, setAppTheme] = useState(null);
  const active = useActiveStep(stepIds);

  const aiPhones = (
    <div className="contents [&_figcaption]:!text-[#c9cfe3]">
      <Phone screen={{ shot: shots.ideas, caption: "Each idea with its caution", alt: "AI-written self-care ideas for knee pain, each with a caution and a link to its MedlinePlus topic" }} appTheme={appTheme} size="w-[42vw] max-w-[210px] lg:w-[210px]" />
      <div className="pt-10 lg:pt-16">
        <Phone screen={{ shot: shots.suggest, caption: "When to see a doctor", alt: "The suggestions card: when to see a doctor, and what to expect from a knee MRI" }} appTheme={appTheme} size="w-[42vw] max-w-[210px] lg:w-[210px]" />
      </div>
    </div>
  );

  return (
    <div className="w-full text-[black] dark:text-[white] pb-16">
      {/* Hero: text beside a browser window with a phone in front of it on large screens */}
      <section className="w-full max-w-5xl mx-auto px-4 pt-8 lg:pt-14 flex flex-col lg:flex-row items-center gap-10 lg:gap-12">
        <div className="flex-1 min-w-0 flex flex-col items-center lg:items-start text-center lg:text-left">
          <img src={cardArt} alt="" aria-hidden="true" width="500" height="360" className="w-20 h-20 lg:w-24 lg:h-24 rounded-2xl object-cover shadow-md" />
          <p className={`pt-4 text-sm font-semibold tracking-widest uppercase ${accentText}`}>Web app · Self-hosted</p>
          <h1 className="text-[42px] sm:text-5xl lg:text-[56px] leading-tight font-bold pb-3">HealthSearch</h1>
          <p className="text-xl lg:text-[22px] leading-relaxed text-[#333] dark:text-[#d6d9e3]">
            Know before you go. Ask a health question in plain words, understand it from MedlinePlus, see which
            Massachusetts hospitals offer the care and what they charge, and get self-care ideas from an LLM,
            checked by code before you see them.
          </p>
          <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 pt-6">
            <Link to="/healthsearch" className={filledButton}>
              Try it
              <ArrowForwardIcon fontSize="small" aria-hidden="true" />
            </Link>
            <a href="#built" className={outlineButton}>
              How it's built
            </a>
            <Link to="/healthsearch/methodology" className={outlineButton}>
              Methodology
            </Link>
          </div>
          <ul className="flex flex-wrap justify-center lg:justify-start gap-2 pt-6" aria-label="Built with">
            {tech.map((t) => (
              <li key={t} className="rounded-full border border-[#eb4d5c] dark:border-[#c9a7ff] px-3 py-0.5 text-sm font-medium text-[#eb4d5c] dark:text-[#c9a7ff]">
                {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="relative w-full lg:w-[520px] shrink-0 lg:pb-10">
          <Browser
            screen={{ ...WIDE, shot: shots.results, alt: "HealthSearch results for knee MRI near Boston" }}
            appTheme={appTheme}
            size="w-full"
          />
          <div className="hidden lg:block absolute -bottom-2 -right-8">
            <Phone screen={{ shot: shots.phoneResults, alt: "HealthSearch on a phone" }} appTheme={appTheme} size="w-[150px]" />
          </div>
        </div>
      </section>

      {/* What it does, at a glance. Each tile jumps to its part of the tour. */}
      <section className="w-full max-w-5xl mx-auto px-4 pt-14">
        <FeatureTiles features={features} />
      </section>

      {/* AI spotlight: the LLM feature and the engineering around it */}
      <section id="ai" className="scroll-mt-4 w-full mt-16 bg-[#0f193d] text-white" aria-labelledby="ai-heading">
        <div className="relative overflow-hidden">
          <div aria-hidden="true" className="pointer-events-none absolute -left-24 -top-24 h-80 w-80 rounded-full bg-[#f07364] opacity-20 blur-3xl dark:bg-[#c9a7ff]" />
          <div aria-hidden="true" className="pointer-events-none absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-[#c9a7ff] opacity-15 blur-3xl" />
          <div className="relative w-full max-w-5xl mx-auto px-4 py-14 lg:py-20 flex flex-col lg:flex-row gap-10 lg:gap-12">
            <div className="flex-1 min-w-0">
              <p className="flex items-center justify-center lg:justify-start gap-2 text-sm font-semibold tracking-widest uppercase text-[#ffb3a9] dark:text-[#d8bfff]">
                <AutoAwesomeIcon sx={{ fontSize: 18 }} aria-hidden="true" />
                AI spotlight · LLM integration
              </p>
              <h2 id="ai-heading" className="text-4xl lg:text-5xl font-bold pt-2 pb-4 text-center lg:text-left">
                An LLM feature built to be trusted
              </h2>
              <p className="text-lg lg:text-xl leading-relaxed text-[#d6d9e3] text-center lg:text-left">
                Click "Get suggestions" and an LLM writes self-care ideas for what you searched: over-the-counter
                options, exercises, stretches and diet changes, each with a caution, a link to its MedlinePlus topic, and
                when to see a doctor. For a procedure you picked, it adds what to expect. The model writes; code decides
                what you see.
              </p>
              {/* Phones: the screenshots come right after the introduction */}
              <div className="flex lg:hidden justify-center gap-4 pt-8">{aiPhones}</div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-8">
                {aiPoints.map(({ icon: Icon, title, text }) => (
                  <li key={title} className="rounded-2xl bg-white/[0.06] p-5 ring-1 ring-white/10">
                    <span className="flex items-center gap-2.5">
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#f07364]/20 text-[#ffb3a9] dark:bg-[#c9a7ff]/20 dark:text-[#d8bfff]">
                        <Icon fontSize="small" aria-hidden="true" />
                      </span>
                      <span className="text-lg font-semibold">{title}</span>
                    </span>
                    <span className="block pt-2 text-[15px] leading-relaxed text-[#c9cfe3]">{text}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 pt-8">
                <Link
                  to="/healthsearch"
                  className="inline-flex items-center gap-2 rounded-full bg-[#f07364] px-5 py-2.5 text-lg font-semibold text-white transition-colors hover:bg-[#e2584a] dark:bg-[#c9a7ff] dark:text-[#0f193d] dark:hover:bg-[#d8bfff]"
                >
                  Try a search, then Get suggestions
                  <ArrowForwardIcon fontSize="small" aria-hidden="true" />
                </Link>
              </div>
            </div>
            {/* Large screens: the screenshots sit beside the text */}
            <div className="hidden lg:flex lg:items-start gap-5 pt-6">{aiPhones}</div>
          </div>
        </div>
      </section>

      {/* The tour */}
      <section className="w-full pt-16" aria-labelledby="tour-heading">
        <div className="w-full max-w-5xl mx-auto px-4">
          <h2 id="tour-heading" className="text-4xl lg:text-5xl font-bold pb-2 text-center lg:text-left">Take the tour</h2>
          <p className="text-lg lg:text-xl text-center lg:text-left pb-6">
            One search, "knee MRI near Boston", from the question to the price.
          </p>
        </div>

        <TourBar steps={steps} active={active} appTheme={appTheme} setAppTheme={setAppTheme} />

        <ol className="w-full max-w-5xl mx-auto px-4">
          {steps.map((step, i) => (
            <TourStep key={step.id} step={step} index={i} total={steps.length} appTheme={appTheme} />
          ))}
        </ol>
      </section>

      {/* Engineering notes for readers who want to know how it works */}
      <section id="built" className="scroll-mt-6 w-full max-w-5xl mx-auto px-4 pt-10" aria-labelledby="built-heading">
        <h2 id="built-heading" className="text-4xl lg:text-5xl font-bold pb-2 text-center lg:text-left">How it's built</h2>
        <p className="text-lg lg:text-xl text-center lg:text-left pb-7">
          Public data and a home server.
        </p>
        <ul className="grid grid-cols-2 lg:grid-cols-4 gap-4 pb-6">
          {numbers.map((n) => (
            <li key={n.label} className="rounded-2xl p-5 bg-white dark:bg-white/5 shadow-sm ring-1 ring-black/5 dark:ring-white/10 text-center lg:text-left">
              <span className={`block text-3xl lg:text-4xl font-bold ${accentText}`}>{n.value}</span>
              <span className={`block pt-1 text-[15px] leading-snug ${mutedText}`}>{n.label}</span>
            </li>
          ))}
        </ul>
        <InternalsGrid items={internals} />
        <div className="flex flex-wrap items-center justify-center gap-3 pt-10">
          <Link to="/healthsearch" className={filledButton}>
            Try HealthSearch
            <ArrowForwardIcon fontSize="small" aria-hidden="true" />
          </Link>
          <Link to="/healthsearch/methodology" className={outlineButton}>
            Read the methodology
          </Link>
        </div>
        <p className={`pt-8 text-center ${mutedText}`}>
          Built by Mohamed Oulal in 2026, on personal time and hardware, from public data: MedlinePlus, CMS, the US
          Census, Cost Plus Drugs and Massachusetts hospitals' price files.
        </p>
        <p className={`pt-2 text-center text-sm ${mutedText}`}>Not medical advice. A published price is not your final bill.</p>
      </section>
    </div>
  );
}

export default HealthSearchProject;
